import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Image,
  Input,
  Stack,
  Text,
  useToast
} from '@chakra-ui/react';
import {
  FaBriefcase,
  FaEnvelope,
  FaIdCard,
  FaMapMarkerAlt,
  FaPhone,
  FaUpload,
  FaUser,
  FaWhatsapp
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../api';
import PageLayout from './PageLayout';

const PerfilPage = ({ user }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  const [profissao, setProfissao] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchProfissao = async () => {
      if (!user?.id_pessoa) {
        setProfissao('');
        return;
      }
      try {
        const response = await fetch(`${API_BASE}/getProfissaoPessoa?id_pessoa=${user.id_pessoa}`);
        const data = await response.json();
        const row = data?.result?.[0]?.[0];
        setProfissao(row?.profissao || row?.ramo || '');
      } catch (err) {
        setProfissao('');
      }
    };

    fetchProfissao();
  }, [user?.id_pessoa]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  };

  const handleUpload = async () => {
    if (!file || !user) return;
    const formData = new FormData();
    formData.append('foto', file);
    formData.append('id_pessoa', user.id_pessoa);

    try {
      await fetch(`${API_BASE}/uploadFoto`, {
        method: 'POST',
        body: formData
      });
      toast({
        title: 'Foto atualizada',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      setFile(null);
    } catch (err) {
      toast({
        title: 'Erro ao enviar foto',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  return (
    <PageLayout
      title={user ? `Ir\u2234 ${user.nome}` : 'Irmao'}
      subtitle="Meu perfil"
      onBack={() => navigate('/')}
      contentMaxW="820px"
      accent="#93C8E6"
      background="#EEF3F7"
      watermark="/maconaria.png"
    >
      <Stack spacing={6}>
        <Box
          bg="white"
          borderRadius="22px"
          p={6}
          boxShadow="0 14px 30px rgba(15,23,42,0.22)"
        >
          <Stack spacing={4} align="center">
            <Text fontWeight="bold" color="gray.800">Sua foto</Text>
            <Box
              width="160px"
              height="160px"
              borderRadius="32px"
              overflow="hidden"
              boxShadow="0 12px 24px rgba(15,23,42,0.25)"
              bg="#f2f4f7"
            >
              <Image
                src={preview || `${API_BASE}/Fotos/${user?.id_pessoa}.png`}
                alt={user ? user.nome : 'Perfil'}
                width="100%"
                height="100%"
                objectFit="cover"
              />
            </Box>
            <FormControl width="100%">
              <FormLabel fontSize="sm" color="gray.600">Trocar foto</FormLabel>
              <Input type="file" accept="image/*" onChange={handleFileChange} />
            </FormControl>
            <Flex direction={{ base: 'column', sm: 'row' }} gap={3} width="100%">
              <Button
                leftIcon={<FaUpload />}
                colorScheme="blue"
                onClick={handleUpload}
                isDisabled={!file}
                flex="1"
              >
                Enviar nova foto
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setPreview(null);
                  setFile(null);
                }}
                isDisabled={!file}
                flex={{ base: '1', sm: 'unset' }}
              >
                Cancelar
              </Button>
            </Flex>
          </Stack>
        </Box>

        <Box
          bg="white"
          borderRadius="22px"
          p={6}
          boxShadow="0 14px 30px rgba(15,23,42,0.22)"
        >
          <Text fontWeight="bold" color="gray.800" mb={4}>Dados do membro</Text>
          <Stack spacing={3}>
            <HStack spacing={3}>
              <FaUser color="#1f4b99" />
              <Text color="gray.500">Nome</Text>
              <Text fontWeight="semibold" color="gray.800">{user?.nome || '-'}</Text>
            </HStack>
            <HStack spacing={3}>
              <FaIdCard color="#1f4b99" />
              <Text color="gray.500">CPF</Text>
              <Text fontWeight="semibold" color="gray.800">{user?.cpf || '-'}</Text>
            </HStack>
            <HStack spacing={3}>
              <FaBriefcase color="#1f4b99" />
              <Text color="gray.500">Profissao</Text>
              <Text fontWeight="semibold" color="gray.800">{profissao || user?.profissao || '-'}</Text>
            </HStack>
          </Stack>
        </Box>

        <Box
          bg="white"
          borderRadius="22px"
          p={6}
          boxShadow="0 14px 30px rgba(15,23,42,0.22)"
        >
          <Text fontWeight="bold" color="gray.800" mb={4}>Contato</Text>
          <Stack spacing={3}>
            <HStack spacing={3}>
              <FaPhone color="#1f4b99" />
              <Text color="gray.500">Telefone</Text>
              <Text fontWeight="semibold" color="gray.800">{user?.fone || '-'}</Text>
            </HStack>
            <HStack spacing={3}>
              <FaWhatsapp color="#1f4b99" />
              <Text color="gray.500">WhatsApp</Text>
              <Text fontWeight="semibold" color="gray.800">{user?.whatsapp || '-'}</Text>
            </HStack>
            <HStack spacing={3}>
              <FaEnvelope color="#1f4b99" />
              <Text color="gray.500">Email</Text>
              <Text fontWeight="semibold" color="gray.800">{user?.email || '-'}</Text>
            </HStack>
          </Stack>
        </Box>

        <Box
          bg="white"
          borderRadius="22px"
          p={6}
          boxShadow="0 14px 30px rgba(15,23,42,0.22)"
        >
          <Text fontWeight="bold" color="gray.800" mb={4}>Endereco</Text>
          <Stack spacing={3}>
            <HStack spacing={3}>
              <FaMapMarkerAlt color="#1f4b99" />
              <Text color="gray.500">Endereco</Text>
              <Text fontWeight="semibold" color="gray.800">{user?.endereco || '-'}</Text>
            </HStack>
            <HStack spacing={3}>
              <FaMapMarkerAlt color="#1f4b99" />
              <Text color="gray.500">Cidade</Text>
              <Text fontWeight="semibold" color="gray.800">{user?.cidade || '-'}</Text>
            </HStack>
          </Stack>
        </Box>
      </Stack>
    </PageLayout>
  );
};

export default PerfilPage;
